/**
 * @module data/mockData
 * @description Datos mock para el modo demo del sistema ACSGC (sin conexión a Supabase).
 *
 * Provee datasets completos y realistas para que la aplicación funcione
 * correctamente en modo demo con credenciales `admin@company.com` / `admin123`.
 *
 * ## Datos incluidos
 * | Export | Descripción |
 * |--------|-------------|
 * | `mockHierarchicalUsers` | Usuarios de prueba en 3 niveles jerárquicos (MINSAL → SS → Hospital) |
 * | `mockOrganizations` | Organizaciones de la jerarquía ACSGC (MINSAL, SS, Hospitales) |
 * | `mockTechnicalReferents` | Referentes técnicos asignables a controles |
 * | `mockControls` | Controles ISO 27002 con estado, tareas y evidencias pre-cargadas |
 * | `mockNotifications` | Notificaciones in-app de ejemplo |
 *
 * ## Credenciales de prueba
 * Todos los usuarios usan password: `Admin1234`. Ver tabla completa en `mockHierarchicalUsers`.
 *
 * ## Uso en contextos
 * Los contextos (DataContext, AuthContext, etc.) detectan automáticamente la ausencia
 * de conexión a Supabase y cargan estos datos como fallback. No hay código condicional
 * en los componentes — el comportamiento es transparente para la UI.
 *
 * @see DataContext para el mecanismo de fallback a datos mock
 * @see AuthContext para las credenciales de demo
 */
import { ISOControl, Notification, TechnicalReferent, Organization, User } from '../types/iso';

// =====================================================
// USUARIOS DE PRUEBA JERÁRQUICOS
// =====================================================

/**
 * Usuarios de prueba para los diferentes niveles de la jerarquía organizacional.
 *
 * Credenciales (todos usan password: Admin1234):
 *
 * | Email                        | Nivel | Organización              | Rol              | Dashboard |
 * |------------------------------|-------|---------------------------|------------------|-----------|
 * | ciso.nacional@minsal.cl      | 1     | MINSAL                    | admin            | Nacional  |
 * | director.ssmo@minsal.cl      | 2     | SSM Occidente             | admin            | Red       |
 * | director.ssmn@minsal.cl      | 2     | SSM Norte                 | admin            | Red       |
 * | ciso.hsjd@minsal.cl          | 3     | Hospital San Juan de Dios | admin            | Local     |
 * | ciso.hrdr@minsal.cl          | 3     | Hospital Roberto del Río  | admin            | Local     |
 * | auditor.hsjd@minsal.cl       | 3     | Hospital San Juan de Dios | auditor          | Local     |
 * | referente.hsjd@minsal.cl     | 3     | Hospital San Juan de Dios | technical_referent | Local   |
 * | admin@company.com            | 3     | Hospital San Juan de Dios | admin            | Local     | (legacy)
 */
export const mockHierarchicalUsers: User[] = [
  // Nivel 1 - MINSAL (Nacional)
  {
    id: 'u1000000-0000-0000-0000-000000000001',
    organizationId: 'a0000000-0000-0000-0000-000000000001',
    name: 'CISO Nacional - MINSAL',
    email: 'ciso.nacional@minsal.cl',
    role: 'admin',
    unit: 'Seguridad de la Información',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    metadata: {
      dashboardLevel: 'national',
      canPushPolicies: true,
      canCreateGlobalTasks: true
    }
  },
  // Nivel 2 - Servicios de Salud (Red)
  {
    id: 'u2000000-0000-0000-0000-000000000001',
    organizationId: 'b0000000-0000-0000-0000-000000000001',
    name: 'Director de TI - SSM Occidente',
    email: 'director.ssmo@minsal.cl',
    role: 'admin',
    unit: 'Dirección de TI',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    metadata: {
      dashboardLevel: 'network',
      serviceName: 'SSM Occidente'
    }
  },
  {
    id: 'u2000000-0000-0000-0000-000000000002',
    organizationId: 'b0000000-0000-0000-0000-000000000002',
    name: 'Director de TI - SSM Norte',
    email: 'director.ssmn@minsal.cl',
    role: 'admin',
    unit: 'Dirección de TI',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    metadata: {
      dashboardLevel: 'network',
      serviceName: 'SSM Norte'
    }
  },
  // Nivel 3 - Hospitales (Local)
  {
    id: 'u3000000-0000-0000-0000-000000000001',
    organizationId: 'c0000000-0000-0000-0000-000000000001',
    name: 'CISO - Hospital San Juan de Dios',
    email: 'ciso.hsjd@minsal.cl',
    role: 'admin',
    unit: 'Seguridad TI',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    metadata: {
      dashboardLevel: 'local',
      hospitalName: 'Hospital San Juan de Dios'
    }
  },
  {
    id: 'u3000000-0000-0000-0000-000000000002',
    organizationId: 'c0000000-0000-0000-0000-000000000002',
    name: 'CISO - Hospital Roberto del Río',
    email: 'ciso.hrdr@minsal.cl',
    role: 'admin',
    unit: 'Seguridad TI',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    metadata: {
      dashboardLevel: 'local',
      hospitalName: 'Hospital Roberto del Río'
    }
  },
  {
    id: 'u3000000-0000-0000-0000-000000000003',
    organizationId: 'c0000000-0000-0000-0000-000000000001',
    name: 'Auditor TI - Hospital San Juan de Dios',
    email: 'auditor.hsjd@minsal.cl',
    role: 'auditor',
    unit: 'Auditoría Interna',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    metadata: {
      dashboardLevel: 'local',
      hospitalName: 'Hospital San Juan de Dios',
      readOnly: true
    }
  },
  {
    id: 'u3000000-0000-0000-0000-000000000004',
    organizationId: 'c0000000-0000-0000-0000-000000000001',
    name: 'Referente Técnico - Hospital San Juan de Dios',
    email: 'referente.hsjd@minsal.cl',
    role: 'technical_referent',
    unit: 'Infraestructura TI',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    metadata: {
      dashboardLevel: 'local',
      hospitalName: 'Hospital San Juan de Dios',
      specializations: ['redes', 'servidores', 'seguridad']
    }
  },
  // Usuario legacy para compatibilidad
  {
    id: 'user-admin-legacy',
    organizationId: 'c0000000-0000-0000-0000-000000000001',
    name: 'Admin Demo',
    email: 'admin@company.com',
    role: 'admin',
    unit: 'Administración',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    metadata: {
      dashboardLevel: 'local',
      isLegacy: true
    }
  }
];

// =====================================================
// ORGANIZACIONES JERÁRQUICAS
// =====================================================

export const mockOrganizations: Organization[] = [
  // Nivel 1 - MINSAL
  {
    id: 'a0000000-0000-0000-0000-000000000001',
    name: 'Ministerio de Salud de Chile',
    slug: 'minsal',
    orgType: 'ministry',
    level: 1,
    parentId: undefined,
    regionCode: undefined,
    isActive: true,
    isCritical: true,
    description: 'Ministerio de Salud - Nivel Central',
    bedCount: 0,
    populationServed: 19000000,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  // Nivel 2 - Servicios de Salud
  {
    id: 'b0000000-0000-0000-0000-000000000001',
    name: 'Servicio de Salud Metropolitano Occidente',
    slug: 'ssmo',
    orgType: 'health_service',
    level: 2,
    parentId: 'a0000000-0000-0000-0000-000000000001',
    regionCode: 'RM',
    isActive: true,
    isCritical: true,
    description: 'Servicio de Salud que atiende las comunas del sector poniente de Santiago',
    bedCount: 0,
    populationServed: 1200000,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'b0000000-0000-0000-0000-000000000002',
    name: 'Servicio de Salud Metropolitano Norte',
    slug: 'ssmn',
    orgType: 'health_service',
    level: 2,
    parentId: 'a0000000-0000-0000-0000-000000000001',
    regionCode: 'RM',
    isActive: true,
    isCritical: true,
    description: 'Servicio de Salud que atiende las comunas del sector norte de Santiago',
    bedCount: 0,
    populationServed: 900000,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'b0000000-0000-0000-0000-000000000003',
    name: 'Fondo Nacional de Salud',
    slug: 'fonasa',
    orgType: 'autonomous_entity',
    level: 2,
    parentId: 'a0000000-0000-0000-0000-000000000001',
    regionCode: undefined,
    isActive: true,
    isCritical: true,
    description: 'Fondo Nacional de Salud - Organismo Autónomo',
    bedCount: 0,
    populationServed: 14000000,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  // Nivel 3 - Hospitales
  {
    id: 'c0000000-0000-0000-0000-000000000001',
    name: 'Hospital San Juan de Dios',
    slug: 'hsjd',
    orgType: 'hospital_high_complexity',
    level: 3,
    parentId: 'b0000000-0000-0000-0000-000000000001',
    regionCode: 'RM',
    isActive: true,
    isCritical: true,
    description: 'Hospital de Alta Complejidad - Servicio de Salud Metropolitano Occidente',
    bedCount: 500,
    populationServed: 400000,
    deisCode: 'HOS-RM-001',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'c0000000-0000-0000-0000-000000000002',
    name: 'Hospital de Niños Roberto del Río',
    slug: 'hrdr',
    orgType: 'hospital_high_complexity',
    level: 3,
    parentId: 'b0000000-0000-0000-0000-000000000002',
    regionCode: 'RM',
    isActive: true,
    isCritical: true,
    description: 'Hospital Pediátrico de Alta Complejidad - Servicio de Salud Metropolitano Norte',
    bedCount: 300,
    populationServed: 350000,
    deisCode: 'HOS-RM-002',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'c0000000-0000-0000-0000-000000000003',
    name: 'CESFAM Maipú',
    slug: 'cesfam-maipu',
    orgType: 'cesfam',
    level: 3,
    parentId: 'b0000000-0000-0000-0000-000000000001',
    regionCode: 'RM',
    isActive: true,
    isCritical: false,
    description: 'Centro de Salud Familiar Maipú',
    bedCount: 0,
    populationServed: 50000,
    deisCode: 'CES-RM-001',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'c0000000-0000-0000-0000-000000000004',
    name: 'Hospital Félix Bulnes',
    slug: 'hfb',
    orgType: 'hospital_medium_complexity',
    level: 3,
    parentId: 'b0000000-0000-0000-0000-000000000001',
    regionCode: 'RM',
    isActive: true,
    isCritical: false,
    description: 'Hospital de Mediana Complejidad - Servicio de Salud Metropolitano Occidente',
    bedCount: 200,
    populationServed: 250000,
    deisCode: 'HOS-RM-003',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  }
];

/**
 * Función helper para obtener usuario por email
 */
export const getMockUserByEmail = (email: string): User | undefined => {
  return mockHierarchicalUsers.find(u => u.email === email);
};

/**
 * Función helper para obtener organización por id
 */
export const getMockOrganizationById = (id: string): Organization | undefined => {
  return mockOrganizations.find(o => o.id === id);
};

/**
 * Función helper para obtener hijos de una organización
 */
export const getMockOrganizationChildren = (parentId: string): Organization[] => {
  return mockOrganizations.filter(o => o.parentId === parentId);
};

/**
 * Función helper para obtener todos los descendientes de una organización
 */
export const getMockOrganizationDescendants = (parentId: string): Organization[] => {
  const children = getMockOrganizationChildren(parentId);
  let descendants = [...children];

  for (const child of children) {
    descendants = [...descendants, ...getMockOrganizationDescendants(child.id)];
  }

  return descendants;
};

// Technical referents based on the organizational structure
export const mockTechnicalReferents: TechnicalReferent[] = [
  {
    id: 'ref-1',
    name: 'Equipo SIC',
    email: 'sic@company.com',
    role: 'technical_referent',
    unit: 'Seguridad de la Información',
    createdAt: '2024-01-01T00:00:00Z',
    isActive: true,
    assignedControls: [],
    avatar: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2'
  },
  {
    id: 'ref-2',
    name: 'Dirección',
    email: 'direccion@company.com',
    role: 'technical_referent',
    unit: 'Dirección General',
    createdAt: '2024-01-01T00:00:00Z',
    isActive: true,
    assignedControls: [],
    avatar: 'https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2'
  },
  {
    id: 'ref-3',
    name: 'Juan Pablo Galaz',
    email: 'jpgalaz@company.com',
    role: 'technical_referent',
    unit: 'Relaciones Institucionales',
    createdAt: '2024-01-01T00:00:00Z',
    isActive: true,
    assignedControls: [],
    avatar: 'https://images.pexels.com/photos/2182970/pexels-photo-2182970.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2'
  },
  {
    id: 'ref-4',
    name: 'Subdirección de recursos físicos',
    email: 'recursos.fisicos@company.com',
    role: 'technical_referent',
    unit: 'Recursos Físicos',
    createdAt: '2024-01-01T00:00:00Z',
    isActive: true,
    assignedControls: [],
    avatar: 'https://images.pexels.com/photos/3184338/pexels-photo-3184338.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2'
  },
  {
    id: 'ref-5',
    name: 'Informática',
    email: 'informatica@company.com',
    role: 'technical_referent',
    unit: 'Tecnologías de la Información',
    createdAt: '2024-01-01T00:00:00Z',
    isActive: true,
    assignedControls: [],
    avatar: 'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2'
  },
  {
    id: 'ref-6',
    name: 'Subdirección de gestión de las personas',
    email: 'gestion.personas@company.com',
    role: 'technical_referent',
    unit: 'Recursos Humanos',
    createdAt: '2024-01-01T00:00:00Z',
    isActive: true,
    assignedControls: [],
    avatar: 'https://images.pexels.com/photos/3184639/pexels-photo-3184639.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2'
  },
  {
    id: 'ref-7',
    name: 'Asesoría Jurídica',
    email: 'asesoria.juridica@company.com',
    role: 'technical_referent',
    unit: 'Asesoría Legal',
    createdAt: '2024-01-01T00:00:00Z',
    isActive: true,
    assignedControls: [],
    avatar: 'https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2'
  },
  {
    id: 'ref-8',
    name: 'Auditoria',
    email: 'auditoria@company.com',
    role: 'technical_referent',
    unit: 'Auditoría Interna',
    createdAt: '2024-01-01T00:00:00Z',
    isActive: true,
    assignedControls: [],
    avatar: 'https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2'
  },
  {
    id: 'ref-9',
    name: 'Departamento de coordinación de emergencias y desastres',
    email: 'emergencias@company.com',
    role: 'technical_referent',
    unit: 'Gestión de Emergencias',
    createdAt: '2024-01-01T00:00:00Z',
    isActive: true,
    assignedControls: [],
    avatar: 'https://images.pexels.com/photos/3184317/pexels-photo-3184317.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2'
  },
  {
    id: 'ref-10',
    name: 'Departamento de recursos físicos',
    email: 'depto.recursos.fisicos@company.com',
    role: 'technical_referent',
    unit: 'Infraestructura',
    createdAt: '2024-01-01T00:00:00Z',
    isActive: true,
    assignedControls: [],
    avatar: 'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2'
  }
];

// Helper function to get referents by name
const getReferentsByName = (names: string[]): TechnicalReferent[] => {
  return names.map(name => {
    const referent = mockTechnicalReferents.find(r => r.name === name);
    if (!referent) {
      console.warn(`Referent not found: ${name}`);
      return mockTechnicalReferents[0]; // fallback
    }
    return referent;
  }).filter(Boolean);
};

// Complete ISO 27001/27002 controls based on the provided structure
export const mockISOControls: ISOControl[] = [
  // Controles Organizativos (5.1 - 5.37)
  {
    id: 'ctrl-5-1',
    code: '5.1',
    title: 'Políticas de seguridad de la información',
    description: 'Se debe definir un conjunto de políticas para la seguridad de la información, aprobado por la dirección, publicado y comunicado a empleados y partes externas relevantes.',
    category: 'Controles Organizativos',
    subcategory: 'Políticas de seguridad',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC']),
    dueDate: '2024-06-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-2',
    code: '5.2',
    title: 'Funciones y responsabilidades de seguridad de la información',
    description: 'Las responsabilidades de seguridad de la información deben ser definidas y asignadas de acuerdo con las necesidades de la organización.',
    category: 'Controles Organizativos',
    subcategory: 'Organización interna',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'high',
    assignedReferents: getReferentsByName(['Dirección']),
    dueDate: '2024-07-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-3',
    code: '5.3',
    title: 'Segregación de funciones',
    description: 'Las funciones conflictivas y áreas de responsabilidad deben ser segregadas para reducir las oportunidades de modificación no autorizada o no intencional o el uso indebido de los activos de la organización.',
    category: 'Controles Organizativos',
    subcategory: 'Organización interna',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Dirección']),
    dueDate: '2024-08-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-4',
    code: '5.4',
    title: 'Responsabilidades de gestión',
    description: 'La dirección debe requerir a todo el personal que aplique la seguridad de la información de acuerdo con las políticas y procedimientos establecidos de la organización.',
    category: 'Controles Organizativos',
    subcategory: 'Organización interna',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Dirección']),
    dueDate: '2024-08-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-5',
    code: '5.5',
    title: 'Contacto con autoridades',
    description: 'Se deben mantener contactos apropiados con las autoridades pertinentes.',
    category: 'Controles Organizativos',
    subcategory: 'Contactos externos',
    implementation: 'mandatory',
    status: 'completed',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Juan Pablo Galaz']),
    dueDate: '2024-05-01',
    completionDate: '2024-04-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-6',
    code: '5.6',
    title: 'Contacto con grupos de interés especial',
    description: 'Se deben mantener contactos apropiados con grupos de interés especial u otros foros especializados de seguridad y asociaciones profesionales.',
    category: 'Controles Organizativos',
    subcategory: 'Contactos externos',
    implementation: 'recommended',
    status: 'in_progress',
    priority: 'low',
    assignedReferents: getReferentsByName(['Juan Pablo Galaz']),
    dueDate: '2024-09-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-7',
    code: '5.7',
    title: 'Inteligencia de amenazas',
    description: 'La información relacionada con amenazas de seguridad de la información debe ser recolectada y analizada para producir inteligencia de amenazas.',
    category: 'Controles Organizativos',
    subcategory: 'Inteligencia de amenazas',
    implementation: 'recommended',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Juan Pablo Galaz']),
    dueDate: '2024-10-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-8',
    code: '5.8',
    title: 'Seguridad de información en gestión de proyectos',
    description: 'La seguridad de la información debe ser integrada en la gestión de proyectos.',
    category: 'Controles Organizativos',
    subcategory: 'Gestión de proyectos',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Subdirección de recursos físicos']),
    dueDate: '2024-08-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-9',
    code: '5.9',
    title: 'Inventario de información y otros activos asociados',
    description: 'Se debe elaborar y mantener un inventario de información y otros activos asociados, incluyendo propietarios.',
    category: 'Controles Organizativos',
    subcategory: 'Gestión de activos',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC']),
    dueDate: '2024-07-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-10',
    code: '5.10',
    title: 'Uso aceptable de información y otros activos asociados',
    description: 'Se deben identificar, documentar e implementar reglas para el uso aceptable de información y de activos asociados con información e instalaciones de procesamiento de información.',
    category: 'Controles Organizativos',
    subcategory: 'Gestión de activos',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC']),
    dueDate: '2024-08-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-11',
    code: '5.11',
    title: 'Retorno de activos',
    description: 'Todo el personal y usuarios de partes externas deben devolver todos los activos de la organización que estén en su poder al terminar su empleo, contrato o acuerdo.',
    category: 'Controles Organizativos',
    subcategory: 'Gestión de activos',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC']),
    dueDate: '2024-09-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-12',
    code: '5.12',
    title: 'Clasificación de información',
    description: 'La información debe ser clasificada de acuerdo con las necesidades de seguridad de la información de la organización con base en confidencialidad, integridad, disponibilidad y requisitos de las partes interesadas pertinentes.',
    category: 'Controles Organizativos',
    subcategory: 'Clasificación de información',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC']),
    dueDate: '2024-06-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-13',
    code: '5.13',
    title: 'Etiquetado de información',
    description: 'Se debe desarrollar e implementar un conjunto apropiado de procedimientos para el etiquetado de información de acuerdo con el esquema de clasificación de información adoptado por la organización.',
    category: 'Controles Organizativos',
    subcategory: 'Clasificación de información',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC']),
    dueDate: '2024-07-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-14',
    code: '5.14',
    title: 'Transferencia de información',
    description: 'Las reglas, procedimientos o acuerdos de transferencia de información deben estar establecidos para todos los tipos de instalaciones de transferencia dentro de la organización y entre la organización y otras partes.',
    category: 'Controles Organizativos',
    subcategory: 'Manejo de información',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC']),
    dueDate: '2024-08-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-15',
    code: '5.15',
    title: 'Control de acceso',
    description: 'Las reglas para controlar el acceso físico y lógico a información y otras instalaciones de procesamiento de información asociadas deben ser establecidas y implementadas con base en los requisitos del negocio y de seguridad de la información.',
    category: 'Controles Organizativos',
    subcategory: 'Control de acceso',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'high',
    assignedReferents: getReferentsByName(['Informática']),
    dueDate: '2024-06-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-16',
    code: '5.16',
    title: 'Gestión de identidad',
    description: 'El ciclo de vida completo de las identidades debe ser gestionado.',
    category: 'Controles Organizativos',
    subcategory: 'Gestión de identidad',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'high',
    assignedReferents: getReferentsByName(['Subdirección de gestión de las personas']),
    dueDate: '2024-07-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-17',
    code: '5.17',
    title: 'Información de autenticación',
    description: 'La asignación y gestión de información de autenticación debe ser controlada por un proceso de gestión, incluyendo asesorar al personal sobre el manejo apropiado de información de autenticación.',
    category: 'Controles Organizativos',
    subcategory: 'Autenticación',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC']),
    dueDate: '2024-07-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-18',
    code: '5.18',
    title: 'Derechos de acceso',
    description: 'Los derechos de acceso a información y otros activos asociados deben ser suministrados, revisados, modificados y removidos de acuerdo con la política de control de acceso de la organización y las reglas para el negocio.',
    category: 'Controles Organizativos',
    subcategory: 'Gestión de acceso',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC']),
    dueDate: '2024-06-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-19',
    code: '5.19',
    title: 'Seguridad de la información en la relación con los proveedores',
    description: 'Los procesos y procedimientos para gestionar la seguridad de la información en las relaciones con los proveedores deben ser definidos e implementados.',
    category: 'Controles Organizativos',
    subcategory: 'Relaciones con proveedores',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Subdirección de recursos físicos']),
    dueDate: '2024-08-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-20',
    code: '5.20',
    title: 'Acercamiento a la seguridad de la información en acuerdos con proveedores',
    description: 'Los requisitos de seguridad de la información pertinentes deben ser establecidos y acordados con cada proveedor con base en el tipo de relación de acceso, procesamiento, almacenamiento o comunicación.',
    category: 'Controles Organizativos',
    subcategory: 'Acuerdos con proveedores',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Subdirección de recursos físicos']),
    dueDate: '2024-09-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-21',
    code: '5.21',
    title: 'Gestión de seguridad de la información en la cadena de suministro de ICT',
    description: 'Los procesos y procedimientos para gestionar la seguridad de la información en la cadena de suministro de productos o servicios de ICT deben ser definidos e implementados.',
    category: 'Controles Organizativos',
    subcategory: 'Cadena de suministro ICT',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Subdirección de recursos físicos']),
    dueDate: '2024-10-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-22',
    code: '5.22',
    title: 'Monitoreo',
    description: 'La organización debe monitorear, revisar y evaluar con regularidad los servicios de suministro de proveedores.',
    category: 'Controles Organizativos',
    subcategory: 'Monitoreo de proveedores',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Subdirección de recursos físicos']),
    dueDate: '2024-09-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-23',
    code: '5.23',
    title: 'Seguridad de la información para uso de servicios en la nube',
    description: 'Los procesos para la adquisición, uso, gestión y salida de servicios en la nube deben ser establecidos de acuerdo con los requisitos de seguridad de la información de la organización.',
    category: 'Controles Organizativos',
    subcategory: 'Servicios en la nube',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'high',
    assignedReferents: getReferentsByName(['Informática']),
    dueDate: '2024-08-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-24',
    code: '5.24',
    title: 'Planificación y preparación de gestión de incidentes de seguridad de la información',
    description: 'La organización debe planificar y preparar la gestión de incidentes de seguridad de la información definiendo, estableciendo y comunicando procesos, roles y responsabilidades.',
    category: 'Controles Organizativos',
    subcategory: 'Gestión de incidentes',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-07-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-25',
    code: '5.25',
    title: 'Evaluación y decisión sobre eventos de seguridad de la información',
    description: 'La organización debe evaluar los eventos de seguridad de la información y decidir si deben ser clasificados como incidentes de seguridad de la información.',
    category: 'Controles Organizativos',
    subcategory: 'Evaluación de eventos',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-07-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-26',
    code: '5.26',
    title: 'Respuesta a incidentes de seguridad de la información',
    description: 'Los incidentes de seguridad de la información deben ser respondidos de acuerdo con los procedimientos documentados.',
    category: 'Controles Organizativos',
    subcategory: 'Respuesta a incidentes',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'critical',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-06-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-27',
    code: '5.27',
    title: 'Aprendizaje sobre incidentes de seguridad de la información',
    description: 'El conocimiento obtenido al analizar y resolver incidentes de seguridad de la información debe ser usado para reducir la probabilidad o el impacto de incidentes futuros.',
    category: 'Controles Organizativos',
    subcategory: 'Aprendizaje de incidentes',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-08-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-28',
    code: '5.28',
    title: 'Recolección de pruebas',
    description: 'La organización debe establecer y aplicar procedimientos para la identificación, recolección, adquisición y preservación de información que pueda servir como evidencia.',
    category: 'Controles Organizativos',
    subcategory: 'Evidencia forense',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-09-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-29',
    code: '5.29',
    title: 'Seguridad de la información durante interrupción',
    description: 'La organización debe planificar cómo mantener la seguridad de la información a un nivel apropiado durante la interrupción.',
    category: 'Controles Organizativos',
    subcategory: 'Continuidad del negocio',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-08-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-30',
    code: '5.30',
    title: 'Preparación de ICT para continuidad de actividad',
    description: 'La preparación de ICT debe ser planificada, implementada, mantenida y probada con base en los objetivos de continuidad del negocio y los requisitos de continuidad de ICT.',
    category: 'Controles Organizativos',
    subcategory: 'Continuidad ICT',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-09-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-31',
    code: '5.31',
    title: 'Requisitos legales',
    description: 'Los requisitos legales, estatutarios, reglamentarios y contractuales relevantes para seguridad de la información y el enfoque de la organización para cumplir estos requisitos, deben ser identificados, documentados y mantenidos actualizados.',
    category: 'Controles Organizativos',
    subcategory: 'Cumplimiento legal',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'high',
    assignedReferents: getReferentsByName(['Asesoría Jurídica']),
    dueDate: '2024-07-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-32',
    code: '5.32',
    title: 'Derechos de propiedad intelectual',
    description: 'La organización debe implementar procedimientos apropiados para proteger los derechos de propiedad intelectual.',
    category: 'Controles Organizativos',
    subcategory: 'Propiedad intelectual',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Asesoría Jurídica']),
    dueDate: '2024-08-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-33',
    code: '5.33',
    title: 'Protección de registros',
    description: 'Los registros deben ser protegidos contra pérdida, destrucción, falsificación, acceso no autorizado y liberación no autorizada.',
    category: 'Controles Organizativos',
    subcategory: 'Protección de registros',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-08-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-34',
    code: '5.34',
    title: 'Privacidad y protección de PII',
    description: 'La organización debe identificar y cumplir los requisitos relacionados con la preservación de la privacidad y protección de información de identificación personal (PII) de acuerdo con las leyes y reglamentos aplicables y los requisitos contractuales.',
    category: 'Controles Organizativos',
    subcategory: 'Privacidad',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'high',
    assignedReferents: getReferentsByName(['Asesoría Jurídica']),
    dueDate: '2024-07-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-35',
    code: '5.35',
    title: 'Revisión independiente de seguridad de la información',
    description: 'El enfoque de la organización para gestionar la seguridad de la información y su implementación debe ser revisado de forma independiente a intervalos planificados o cuando ocurran cambios significativos.',
    category: 'Controles Organizativos',
    subcategory: 'Revisión independiente',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Auditoria']),
    dueDate: '2024-12-31',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-36',
    code: '5.36',
    title: 'Cumplimiento de políticas',
    description: 'El cumplimiento con las políticas, procedimientos y normas de seguridad de la información de la organización debe ser revisado con regularidad.',
    category: 'Controles Organizativos',
    subcategory: 'Cumplimiento',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Auditoria']),
    dueDate: '2024-11-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-5-37',
    code: '5.37',
    title: 'Procedimientos operativos documentados',
    description: 'Los procedimientos operativos para instalaciones de procesamiento de información deben ser documentados y puestos a disposición de todo el personal que los necesite.',
    category: 'Controles Organizativos',
    subcategory: 'Documentación',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Auditoria']),
    dueDate: '2024-10-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },

  // Controles de Personas (6.1 - 6.8)
  {
    id: 'ctrl-6-1',
    code: '6.1',
    title: 'Control',
    description: 'Todos los candidatos a empleo deben ser investigados de acuerdo con las leyes, reglamentos y ética pertinentes y deben ser proporcionales a los requisitos del negocio, la clasificación de la información a la cual se va a tener acceso y los riesgos percibidos.',
    category: 'Controles de Personas',
    subcategory: 'Antes del empleo',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Subdirección de gestión de las personas']),
    dueDate: '2024-08-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-6-2',
    code: '6.2',
    title: 'Términos y condiciones de trabajo',
    description: 'Los acuerdos contractuales con empleados y contratistas deben establecer sus responsabilidades y las de la organización para la seguridad de la información.',
    category: 'Controles de Personas',
    subcategory: 'Términos de empleo',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Subdirección de gestión de las personas']),
    dueDate: '2024-08-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-6-3',
    code: '6.3',
    title: 'Concientización',
    description: 'Todo el personal de la organización debe recibir educación y entrenamiento en concientización sobre seguridad de la información apropiados y actualizaciones regulares sobre las políticas y procedimientos de la organización pertinentes para su función de trabajo.',
    category: 'Controles de Personas',
    subcategory: 'Concientización y entrenamiento',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'high',
    assignedReferents: getReferentsByName(['Subdirección de gestión de las personas']),
    dueDate: '2024-07-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-6-4',
    code: '6.4',
    title: 'Proceso disciplinario',
    description: 'Debe haber un proceso disciplinario formal y comunicado para tomar acciones contra el personal que haya cometido una violación de seguridad de la información.',
    category: 'Controles de Personas',
    subcategory: 'Proceso disciplinario',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Asesoría Jurídica']),
    dueDate: '2024-09-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-6-5',
    code: '6.5',
    title: 'Responsabilidades tras el cese o cambio de empleo',
    description: 'Las responsabilidades de seguridad de la información que permanecen válidas después del cese o cambio de empleo deben ser definidas, aplicadas y comunicadas al personal pertinente.',
    category: 'Controles de Personas',
    subcategory: 'Terminación de empleo',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Subdirección de gestión de las personas']),
    dueDate: '2024-08-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-6-6',
    code: '6.6',
    title: 'Acuerdos de confidencialidad o no divulgación',
    description: 'Los acuerdos de confidencialidad o no divulgación que reflejen las necesidades de la organización para la protección de información deben ser identificados, documentados, revisados con regularidad y aplicados.',
    category: 'Controles de Personas',
    subcategory: 'Confidencialidad',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Subdirección de gestión de las personas']),
    dueDate: '2024-09-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-6-7',
    code: '6.7',
    title: 'Trabajo a distancia',
    description: 'Se deben implementar medidas de seguridad cuando el personal está trabajando a distancia para proteger la información a la cual se accede, procesa o almacena fuera de las instalaciones de la organización.',
    category: 'Controles de Personas',
    subcategory: 'Trabajo remoto',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Subdirección de gestión de las personas']),
    dueDate: '2024-06-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-6-8',
    code: '6.8',
    title: 'Informe de eventos de seguridad de la información',
    description: 'La organización debe proporcionar un mecanismo para que el personal informe los eventos de seguridad de la información observados o sospechados a través de canales apropiados en forma oportuna.',
    category: 'Controles de Personas',
    subcategory: 'Reporte de eventos',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC']),
    dueDate: '2024-07-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },

  // Controles Físicos (7.1 - 7.14)
  {
    id: 'ctrl-7-1',
    code: '7.1',
    title: 'Perímetros de seguridad física',
    description: 'Los perímetros de seguridad física deben ser definidos y usados para proteger áreas que contengan información y otras instalaciones de procesamiento de información.',
    category: 'Controles Físicos',
    subcategory: 'Áreas seguras',
    implementation: 'mandatory',
    status: 'completed',
    priority: 'high',
    assignedReferents: getReferentsByName(['Departamento de coordinación de emergencias y desastres']),
    dueDate: '2024-05-01',
    completionDate: '2024-04-20',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-7-2',
    code: '7.2',
    title: 'Acceso físico',
    description: 'Las áreas seguras deben ser protegidas por controles de acceso apropiados para asegurar que solamente se permita el acceso a personal autorizado.',
    category: 'Controles Físicos',
    subcategory: 'Control de acceso físico',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'high',
    assignedReferents: getReferentsByName(['Departamento de coordinación de emergencias y desastres']),
    dueDate: '2024-06-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-7-3',
    code: '7.3',
    title: 'Aseguramiento de oficinas',
    description: 'Se debe diseñar y aplicar seguridad física para oficinas, cuartos e instalaciones.',
    category: 'Controles Físicos',
    subcategory: 'Protección de oficinas',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Departamento de coordinación de emergencias y desastres']),
    dueDate: '2024-08-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-7-4',
    code: '7.4',
    title: 'Supervisión de seguridad física',
    description: 'Las instalaciones deben ser supervisadas continuamente para detectar acceso físico no autorizado.',
    category: 'Controles Físicos',
    subcategory: 'Monitoreo físico',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'high',
    assignedReferents: getReferentsByName(['Departamento de coordinación de emergencias y desastres']),
    dueDate: '2024-07-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-7-5',
    code: '7.5',
    title: 'Protección contra amenazas físicas y medioambientales',
    description: 'Se debe diseñar e implementar protección física contra desastres naturales, ataques maliciosos o accidentes.',
    category: 'Controles Físicos',
    subcategory: 'Protección ambiental',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'high',
    assignedReferents: getReferentsByName(['Departamento de coordinación de emergencias y desastres']),
    dueDate: '2024-08-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-7-6',
    code: '7.6',
    title: 'Trabajo en zonas seguras',
    description: 'Se deben diseñar e implementar medidas de protección para áreas seguras y se debe controlar el acceso a ellas.',
    category: 'Controles Físicos',
    subcategory: 'Áreas de trabajo seguras',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Informática']),
    dueDate: '2024-09-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-7-7',
    code: '7.7',
    title: 'Escritorio y pantalla despejados',
    description: 'Se deben adoptar reglas de escritorio despejado para los papeles y medios de almacenamiento removibles, y una política de pantalla despejada para las instalaciones de procesamiento de información.',
    category: 'Controles Físicos',
    subcategory: 'Política de escritorio limpio',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Informática']),
    dueDate: '2024-08-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-7-8',
    code: '7.8',
    title: 'Ubicación y protección de equipos',
    description: 'Los equipos deben ser ubicados y protegidos para reducir los riesgos de amenazas y peligros del entorno, y las oportunidades de acceso no autorizado.',
    category: 'Controles Físicos',
    subcategory: 'Protección de equipos',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Informática']),
    dueDate: '2024-09-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-7-9',
    code: '7.9',
    title: 'Seguridad de activos fuera de las instalaciones',
    description: 'Se debe proteger los activos fuera de las instalaciones.',
    category: 'Controles Físicos',
    subcategory: 'Activos externos',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Departamento de coordinación de emergencias y desastres']),
    dueDate: '2024-10-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-7-10',
    code: '7.10',
    title: 'Medios de almacenamiento',
    description: 'Los medios de almacenamiento deben ser gestionados de acuerdo con el esquema de clasificación y los requisitos de manejo de la organización.',
    category: 'Controles Físicos',
    subcategory: 'Gestión de medios',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Informática']),
    dueDate: '2024-09-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-7-11',
    code: '7.11',
    title: 'Servicios de apoyo',
    description: 'Las instalaciones de procesamiento de información deben ser implementadas con servicios de apoyo apropiados.',
    category: 'Controles Físicos',
    subcategory: 'Servicios de soporte',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Departamento de coordinación de emergencias y desastres']),
    dueDate: '2024-10-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-7-12',
    code: '7.12',
    title: 'Seguridad de cableado',
    description: 'Los cables que transportan datos o que dan soporte a servicios de información deben ser protegidos contra interceptación, interferencia o daño.',
    category: 'Controles Físicos',
    subcategory: 'Protección de cableado',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Departamento de recursos físicos']),
    dueDate: '2024-11-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-7-13',
    code: '7.13',
    title: 'Mantenimiento de equipo',
    description: 'Los equipos deben ser mantenidos correctamente para asegurar su disponibilidad, integridad y confidencialidad continuas.',
    category: 'Controles Físicos',
    subcategory: 'Mantenimiento',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Informática']),
    dueDate: '2024-10-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-7-14',
    code: '7.14',
    title: 'Eliminación segura o reutilización de equipos',
    description: 'Los elementos de equipos que contengan medios de almacenamiento deben ser verificados para asegurar que cualquier información y software licenciado haya sido retirado o sobrescrito de manera segura antes de su disposición o reutilización.',
    category: 'Controles Físicos',
    subcategory: 'Disposición de equipos',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Informática']),
    dueDate: '2024-11-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },

  // Controles Tecnológicos (8.1 - 8.34)
  {
    id: 'ctrl-8-1',
    code: '8.1',
    title: 'Dispositivos terminales de usuario',
    description: 'La información almacenada en, procesada por o accesible a través de dispositivos terminales de usuario debe ser protegida.',
    category: 'Controles Tecnológicos',
    subcategory: 'Dispositivos de usuario',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-07-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-2',
    code: '8.2',
    title: 'Derechos de acceso privilegiado',
    description: 'La asignación y uso de derechos de acceso privilegiado debe ser restringida y gestionada.',
    category: 'Controles Tecnológicos',
    subcategory: 'Acceso privilegiado',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'critical',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-06-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-3',
    code: '8.3',
    title: 'Restricción de acceso a la información',
    description: 'El acceso a información y funciones de sistemas de aplicaciones debe ser restringido de acuerdo con la política de control de acceso.',
    category: 'Controles Tecnológicos',
    subcategory: 'Control de acceso',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-06-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-4',
    code: '8.4',
    title: 'Acceso a código fuente',
    description: 'El acceso de lectura y escritura a código fuente, herramientas de desarrollo y librerías de software debe ser gestionado apropiadamente.',
    category: 'Controles Tecnológicos',
    subcategory: 'Desarrollo seguro',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Informática']),
    dueDate: '2024-08-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-5',
    code: '8.5',
    title: 'Autenticación segura',
    description: 'Los procedimientos de autenticación segura deben ser implementados con base en las restricciones de acceso a la información y la política de control de acceso.',
    category: 'Controles Tecnológicos',
    subcategory: 'Autenticación',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'high',
    assignedReferents: getReferentsByName(['Informática']),
    dueDate: '2024-07-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-6',
    code: '8.6',
    title: 'Gestión de capacidad',
    description: 'El uso de recursos debe ser supervisado, ajustado y se deben hacer proyecciones de los requisitos de capacidad futura para asegurar el desempeño requerido del sistema.',
    category: 'Controles Tecnológicos',
    subcategory: 'Gestión de capacidad',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Informática', 'Subdirección de gestión de las personas']),
    dueDate: '2024-09-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-7',
    code: '8.7',
    title: 'Protección contra "malware"',
    description: 'Se debe implementar protección contra malware y los usuarios deben ser informados apropiadamente.',
    category: 'Controles Tecnológicos',
    subcategory: 'Protección contra malware',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'critical',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-06-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-8',
    code: '8.8',
    title: 'Gestión de vulnerabilidades técnicas',
    description: 'Se debe obtener información acerca de las vulnerabilidades técnicas de los sistemas de información en uso, evaluar la exposición de la organización a tales vulnerabilidades y tomar las medidas apropiadas.',
    category: 'Controles Tecnológicos',
    subcategory: 'Gestión de vulnerabilidades',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-07-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-9',
    code: '8.9',
    title: 'Gestión de configuración',
    description: 'Se deben establecer, documentar, implementar, supervisar y revisar configuraciones, incluyendo configuraciones de seguridad, para hardware, software, servicios y redes.',
    category: 'Controles Tecnológicos',
    subcategory: 'Gestión de configuración',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-08-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-10',
    code: '8.10',
    title: 'Supresión de información',
    description: 'La información almacenada en sistemas de información, dispositivos o en cualquier otro medio de almacenamiento debe ser suprimida cuando ya no se requiera.',
    category: 'Controles Tecnológicos',
    subcategory: 'Eliminación de información',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-09-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-11',
    code: '8.11',
    title: 'Enmascaramiento de datos',
    description: 'El enmascaramiento de datos debe ser usado de acuerdo con la política de control de acceso de la organización y los requisitos del negocio, tomando en consideración la legislación aplicable.',
    category: 'Controles Tecnológicos',
    subcategory: 'Protección de datos',
    implementation: 'recommended',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-10-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-12',
    code: '8.12',
    title: 'Prevención fuga de datos',
    description: 'Se deben aplicar medidas de prevención de fuga de datos a sistemas, redes y cualquier otro dispositivo que procese, almacene o transmita información.',
    category: 'Controles Tecnológicos',
    subcategory: 'Prevención de fuga de datos',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-08-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-13',
    code: '8.13',
    title: 'Respaldo de información',
    description: 'Se deben mantener copias de respaldo de información, software e imágenes de sistemas, y ponerlas a prueba con regularidad de acuerdo con la política de respaldo acordada.',
    category: 'Controles Tecnológicos',
    subcategory: 'Respaldo',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'critical',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-06-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-14',
    code: '8.14',
    title: 'Redundancia de instalaciones de tratamiento de información',
    description: 'Las instalaciones de procesamiento de información deben ser implementadas con redundancia suficiente para cumplir los requisitos de disponibilidad.',
    category: 'Controles Tecnológicos',
    subcategory: 'Redundancia',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-09-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-15',
    code: '8.15',
    title: 'Registro',
    description: 'Los registros que registran actividades, excepciones, fallas y otros eventos pertinentes deben ser producidos, almacenados, protegidos y analizados.',
    category: 'Controles Tecnológicos',
    subcategory: 'Registro de eventos',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-07-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-16',
    code: '8.16',
    title: 'Actividades de monitoreo',
    description: 'Las redes, sistemas y aplicaciones deben ser supervisados para detectar comportamiento anómalo y se deben tomar las acciones apropiadas para evaluar los incidentes potenciales de seguridad de la información.',
    category: 'Controles Tecnológicos',
    subcategory: 'Monitoreo',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-08-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-17',
    code: '8.17',
    title: 'Sincronización de reloj',
    description: 'Los relojes de todos los sistemas de procesamiento de información pertinentes dentro de la organización y dominio de seguridad deben ser sincronizados con una sola fuente de tiempo de referencia.',
    category: 'Controles Tecnológicos',
    subcategory: 'Sincronización temporal',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-09-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-18',
    code: '8.18',
    title: 'Uso de programas privilegiados de utilidad',
    description: 'El uso de programas de utilidad que pueden ser capaces de anular los controles del sistema y de las aplicaciones debe ser restringido y estrechamente controlado.',
    category: 'Controles Tecnológicos',
    subcategory: 'Utilidades privilegiadas',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-08-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-19',
    code: '8.19',
    title: 'Instalación de "software" en sistemas operativos',
    description: 'Se deben implementar procedimientos y medidas para controlar de manera segura la instalación de software en sistemas operativos.',
    category: 'Controles Tecnológicos',
    subcategory: 'Instalación de software',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-09-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-20',
    code: '8.20',
    title: 'Seguridad de redes',
    description: 'Las redes y los dispositivos de red deben ser gestionados y controlados para proteger la información en sistemas y aplicaciones.',
    category: 'Controles Tecnológicos',
    subcategory: 'Seguridad de red',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-07-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-21',
    code: '8.21',
    title: 'Seguridad de servicios de red',
    description: 'Se deben identificar los mecanismos de seguridad, los niveles de servicio y los requisitos de gestión de todos los servicios de red e incluirlos en los acuerdos de servicios de red, ya sea que los servicios se presten internamente o se contraten externamente.',
    category: 'Controles Tecnológicos',
    subcategory: 'Servicios de red',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-08-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-22',
    code: '8.22',
    title: 'Segregación de redes',
    description: 'Los grupos de servicios de información, usuarios y sistemas de información deben ser segregados en redes.',
    category: 'Controles Tecnológicos',
    subcategory: 'Segregación de red',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-08-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-23',
    code: '8.23',
    title: 'Filtro Web',
    description: 'El acceso a sitios web externos debe ser gestionado para reducir la exposición a contenido malicioso.',
    category: 'Controles Tecnológicos',
    subcategory: 'Filtrado web',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-07-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-24',
    code: '8.24',
    title: 'Uso de criptografía',
    description: 'Se deben definir e implementar reglas para el uso efectivo de criptografía, incluyendo gestión de llaves criptográficas.',
    category: 'Controles Tecnológicos',
    subcategory: 'Criptografía',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'high',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-09-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-25',
    code: '8.25',
    title: 'Ciclo de vida de desarrollo seguro',
    description: 'Se deben establecer y aplicar reglas para el desarrollo seguro de software y sistemas.',
    category: 'Controles Tecnológicos',
    subcategory: 'Desarrollo seguro',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-10-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-26',
    code: '8.26',
    title: 'Requisito de Seguridad de las aplicaciones',
    description: 'Los requisitos de seguridad de la información deben ser identificados, especificados y aprobados al desarrollar o adquirir aplicaciones.',
    category: 'Controles Tecnológicos',
    subcategory: 'Requisitos de seguridad',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-10-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-27',
    code: '8.27',
    title: 'Principios de ingeniería y arquitectura de sistemas seguros',
    description: 'Los principios para la ingeniería de sistemas seguros deben ser establecidos, documentados, mantenidos y aplicados a cualquier actividad de desarrollo de sistemas de información.',
    category: 'Controles Tecnológicos',
    subcategory: 'Arquitectura segura',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-11-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-28',
    code: '8.28',
    title: 'Codificación segura',
    description: 'Se deben aplicar principios de codificación segura al desarrollo de software.',
    category: 'Controles Tecnológicos',
    subcategory: 'Codificación segura',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-11-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-29',
    code: '8.29',
    title: 'Pruebas de seguridad en desarrollo y aceptación',
    description: 'Los procesos de pruebas de seguridad deben ser definidos e implementados en el ciclo de vida del desarrollo.',
    category: 'Controles Tecnológicos',
    subcategory: 'Pruebas de seguridad',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-12-01',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-30',
    code: '8.30',
    title: 'Desarrollo externo',
    description: 'La organización debe dirigir, supervisar y revisar las actividades de desarrollo de sistemas contratadas externamente.',
    category: 'Controles Tecnológicos',
    subcategory: 'Desarrollo externo',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-12-15',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-31',
    code: '8.31',
    title: 'Separación de entornos de desarrollo',
    description: 'Los entornos de desarrollo, pruebas y operación deben ser separados y asegurados.',
    category: 'Controles Tecnológicos',
    subcategory: 'Separación de entornos',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-10-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-32',
    code: '8.32',
    title: 'Gestión de cambio',
    description: 'Los cambios a sistemas de procesamiento de información y aplicaciones deben estar sujetos a procedimientos formales de gestión de cambio.',
    category: 'Controles Tecnológicos',
    subcategory: 'Gestión de cambios',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-11-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-33',
    code: '8.33',
    title: 'Información de prueba',
    description: 'Los datos de prueba deben ser seleccionados, protegidos y controlados cuidadosamente.',
    category: 'Controles Tecnológicos',
    subcategory: 'Datos de prueba',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-12-30',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  },
  {
    id: 'ctrl-8-34',
    code: '8.34',
    title: 'Protección de sistemas de información durante pruebas de auditoría',
    description: 'Las pruebas de auditoría y otras actividades de aseguramiento que involucren la evaluación de sistemas operativos deben ser planificadas y acordadas entre el probador y la gestión apropiada.',
    category: 'Controles Tecnológicos',
    subcategory: 'Pruebas de auditoría',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    dueDate: '2024-12-31',
    evidence: [],
    tasks: [],
    auditTrail: [],
    children: []
  }
];

// Mock notifications
export const mockNotifications: Notification[] = [
  {
    id: 'notif-1',
    title: 'Control 5.26 requiere atención urgente',
    message: 'El control de respuesta a incidentes tiene fecha límite próxima y es de prioridad crítica.',
    type: 'warning',
    timestamp: '2024-01-15T10:30:00Z',
    read: false,
    actionUrl: '/controls/ctrl-5-26',
    actionText: 'Ver Control'
  },
  {
    id: 'notif-2',
    title: 'Nuevos controles asignados',
    message: 'Se han asignado 5 nuevos controles al Equipo SIC para implementación.',
    type: 'info',
    timestamp: '2024-01-14T14:20:00Z',
    read: false,
    actionUrl: '/dashboard',
    actionText: 'Ver Dashboard'
  },
  {
    id: 'notif-3',
    title: 'Control 7.1 completado exitosamente',
    message: 'El control de perímetros de seguridad física ha sido marcado como completado.',
    type: 'success',
    timestamp: '2024-01-13T09:15:00Z',
    read: true,
    actionUrl: '/controls/ctrl-7-1',
    actionText: 'Ver Detalles'
  },
  {
    id: 'notif-4',
    title: 'Revisión de cumplimiento programada',
    message: 'Se ha programado una revisión de cumplimiento para el próximo mes.',
    type: 'info',
    timestamp: '2024-01-12T16:45:00Z',
    read: true
  }
];